import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, Loader, ExternalLink, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { getFrontendUrl } from '../lib/platformDomain';
import { zonedToUtc } from '../lib/timezone';

/**
 * Public Scheduling Page
 * No login required. Applicants click the scheduling link from their email
 * and pick a time slot based on the agent's availability.
 *
 * URL format: /schedule/:token
 */

interface SchedulingData {
  request: {
    requestId: string;
    title: string;
    description?: string;
    eventType: string;
    durationMinutes: number;
    timezone: string;
    applicantName: string;
    proposedDateTime?: string;
    status?: string;
    isResponded?: boolean;
    isExpired?: boolean;
    decision?: string;
    respondedAt?: string;
    rejectionReason?: string;
    confirmedDateTime?: string;
    eventId?: string;
  };
  agent: {
    name: string;
    timezone: string;
  };
  availability: {
    timezone: string;
    weeklySlots: { dayOfWeek: number; startTime: string; endTime: string }[];
    dateOverrides: { date: string; available: boolean; reason?: string }[];
    bufferMinutes: number;
    minNoticeHours: number;
    maxDaysAdvance: number;
  } | null;
  existingEvents: { dateTime: string; durationMinutes: number }[];
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];


export default function PublicScheduleView({ token }: { token: string }) {
  const [data, setData] = useState<SchedulingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [meetingUrl, setMeetingUrl] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  // Invitation workflow state
  const [showPicker, setShowPicker] = useState(false);
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(false);
  const [respondedAction, setRespondedAction] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declinePreset, setDeclinePreset] = useState('');
  const [monthNotice, setMonthNotice] = useState<string | null>(null);

  const DECLINE_PRESETS = ['Scheduling conflict', 'Personal emergency', 'No longer interested', 'Unable to attend'];

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/availability/public/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Failed to load' }));
          setError(err.error || 'Failed to load scheduling data');
          return;
        }
        const d = await res.json();
        setData(d);
        // Revisiting an already-decided link → show the completed state.
        if (d.request?.isResponded || (d.request?.decision && d.request.decision !== 'none')) {
          setResponded(true);
          setRespondedAction(d.request.decision || 'accepted');
          setShowPicker(false);
        }
      } catch (err: any) {
        setError(err.message || 'Network error');
      }
      setLoading(false);
    })();
  }, [token]);

  // Auto-advance to the first month that actually has availability, so users
  // never have to click through empty weeks/months.
  useEffect(() => {
    if (!data || loading || responded) return;
    const avail = data.availability;
    const minDate = new Date(Date.now() + (avail?.minNoticeHours || 24) * 60 * 60 * 1000);
    const maxDate = new Date(Date.now() + (avail?.maxDaysAdvance || 30) * 24 * 60 * 60 * 1000);
    const hasAvailabilityInMonth = (m: number, y: number) => {
      const days = new Date(y, m + 1, 0).getDate();
      for (let day = 1; day <= days; day++) {
        const ds = `${y}-${(m + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        const dd = new Date(ds + 'T12:00:00');
        if (dd < minDate || dd > maxDate) continue;
        const override = avail?.dateOverrides?.find(o => o.date === ds);
        if (override) { if (override.available) return true; continue; }
        if (avail?.weeklySlots?.some(s => s.dayOfWeek === dd.getDay())) return true;
      }
      return false;
    };
    // Only auto-advance forward; never back to the past.
    let m = currentMonth, y = currentYear;
    let guard = 0;
    const startKey = y * 12 + m;
    while (!hasAvailabilityInMonth(m, y) && guard < 24) {
      m += 1;
      if (m > 11) { m = 0; y += 1; }
      guard += 1;
    }
    const endKey = y * 12 + m;
    if (endKey > startKey) {
      setCurrentMonth(m);
      setCurrentYear(y);
      setMonthNotice('No availability in the current month. Showing the next month with available slots.');
    } else if (endKey === startKey) {
      setMonthNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, responded]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-brand mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading scheduling options...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Unable to Load</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const avail = data?.availability;
  const now = new Date();
  const minDate = new Date(now.getTime() + (avail?.minNoticeHours || 24) * 60 * 60 * 1000);
  const maxDate = new Date(now.getTime() + (avail?.maxDaysAdvance || 30) * 24 * 60 * 60 * 1000);

  // Generate available dates in current month
  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay();

  const isDateAvailable = (dateStr: string): boolean => {
    const d = new Date(dateStr + 'T12:00:00');
    if (d < minDate || d > maxDate) return false;

    // Check date overrides
    const override = avail?.dateOverrides?.find(o => o.date === dateStr);
    if (override) return override.available;

    // Check day of week
    const dow = d.getDay();
    return avail?.weeklySlots?.some(s => s.dayOfWeek === dow) || false;
  };

  const getAvailableSlots = (dateStr: string): { startTime: string; endTime: string }[] => {
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();

    // Check override for custom hours
    const override = avail?.dateOverrides?.find(o => o.date === dateStr);
    if (override?.available) {
      if (override.startTime && override.endTime) {
        return [{ startTime: override.startTime, endTime: override.endTime }];
      }
    }

    return avail?.weeklySlots?.filter(s => s.dayOfWeek === dow) || [];
  };

  const isSlotAvailable = (dateStr: string, time: string): boolean => {
    if (!data) return false;
    // Interpret the slot in the organizer's timezone (the availability's tz),
    // then compare against existing events in UTC — prevents false "available"
    // slots when the viewer's browser timezone differs.
    const tz = data.request.timezone || 'America/New_York';
    const slotStart = zonedToUtc(dateStr, time, tz);
    const slotEnd = new Date(slotStart.getTime() + (data.request.durationMinutes || 30) * 60 * 1000);

    // Check against existing events (double-booking prevention)
    for (const ev of data.existingEvents) {
      const evStart = new Date(ev.dateTime);
      const evEnd = new Date(evStart.getTime() + (ev.durationMinutes || 30) * 60 * 1000);
      if (slotStart < evEnd && slotEnd > evStart) return false;
    }
    return true;
  };

  const generateTimeSlots = (dateStr: string): string[] => {
    const slots = getAvailableSlots(dateStr);
    const times: string[] = [];
    const seenTimes = new Set<string>();
    const buffer = avail?.bufferMinutes || 15;

    for (const slot of slots) {
      const [startH, startM] = slot.startTime.split(':').map(Number);
      const [endH, endM] = slot.endTime.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const duration = data?.request.durationMinutes || 30;

      for (let m = startMinutes; m + duration <= endMinutes; m += duration + buffer) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        const timeStr = `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        if (!seenTimes.has(timeStr) && isSlotAvailable(dateStr, timeStr)) {
          seenTimes.add(timeStr);
          times.push(timeStr);
        }
      }
    }
    return times;
  };

  // Respond to the invitation (accept / decline / new_time) — no login needed.
  const respond = async (action: 'accept' | 'decline' | 'new_time') => {
    if (!token) return;
    if (action === 'decline' && !declineReason.trim()) {
      setConfirmError('Please provide a reason for declining before submitting.');
      return;
    }
    setResponding(true);
    setConfirmError(null);
    try {
      const res = await fetch('/api/scheduling/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action,
          reason: action === 'decline' ? declineReason.trim() : undefined,
          selectedDate: action === 'new_time' ? selectedDate : undefined,
          selectedTime: action === 'new_time' ? selectedTime : undefined,
          timezone: data?.request.timezone || 'America/New_York',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || 'Request failed');
      }
      const result = await res.json();
      setRespondedAction(action);
      setResponded(true);
      setShowPicker(false);
      if ((action === 'accept' || action === 'new_time') && result.meetingUrl) setMeetingUrl(result.meetingUrl);
    } catch (err: any) {
      setConfirmError(err.message);
    }
    setResponding(false);
  };

  const proposedDateTime = data?.request.proposedDateTime;
  const proposedDate = proposedDateTime ? new Date(proposedDateTime) : null;
  const proposedTimeLabel = proposedDate
    ? `${proposedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at ${proposedDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : '';

  // Successful response screen (declined / accepted / rescheduled) — also
  // rendered when the decision link is revisited after completion.
  if (responded) {
    const isDeclined = respondedAction === 'decline';
    const isNewTime = respondedAction === 'new_time';
    const decisionReason = data?.request?.rejectionReason || declineReason.trim() || null;
    const decisionAt = data?.request?.respondedAt || null;
    const decidedDate = data?.request?.confirmedDateTime || proposedDateTime || null;
    const decidedDateLabel = decidedDate ? new Date(decidedDate).toLocaleString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : '';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center animate-fade-in">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDeclined ? 'bg-rose-100' : 'bg-emerald-100'}`}>
            {isDeclined ? <AlertCircle className="h-8 w-8 text-rose-500" /> : <CheckCircle className="h-8 w-8 text-emerald-500" />}
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {isDeclined ? 'Invitation Declined' : isNewTime ? 'Meeting Rescheduled' : 'Meeting Confirmed'}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {isDeclined
              ? 'You have declined this meeting. The organizer has been notified.'
              : isNewTime
                ? 'Your meeting has been rescheduled. A confirmation has been sent to all participants.'
                : `Your meeting with ${data?.agent.name} is confirmed.`}
          </p>

          {/* Event details for reference */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Event Details</p>
            <p className="text-sm font-bold text-slate-800">{data?.request.title}</p>
            {decidedDateLabel && <p className="text-sm text-slate-600">{decidedDateLabel}</p>}
            <p className="text-sm text-slate-600">Time zone: {data?.request.timezone}</p>
            <p className="text-sm text-slate-600">Duration: {data?.request.durationMinutes} min</p>
            <p className="text-sm text-slate-600">Organizer: {data?.agent.name}</p>
            {data?.request.description && <p className="text-xs text-slate-500 whitespace-pre-wrap">{data.request.description}</p>}
            {isDeclined && decisionReason && (
              <p className="text-xs font-semibold text-rose-600">Rejection reason: {decisionReason}</p>
            )}
            {isDeclined && decisionAt && (
              <p className="text-xs text-slate-400">Rejected on {new Date(decisionAt).toLocaleString()}</p>
            )}
          </div>

          {!isDeclined && meetingUrl && (
            <a
              href={meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-white rounded-xl font-bold hover:opacity-90 transition-all"
            >
              <Video className="h-4 w-4" />
              Join Virtual Meeting
            </a>
          )}

          {/* Back to Events */}
          <a
            href={`${getFrontendUrl()}/applicant`}
            className="mt-4 inline-block w-full px-6 py-3 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold transition-all"
          >
            ← Back to Events
          </a>
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

  const dates: (string | null)[] = [];
  for (let i = 0; i < firstDay; i++) dates.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
    dates.push(dateStr);
  }

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const timeSlots = selectedDate ? generateTimeSlots(selectedDate) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
            <h1 className="text-lg font-bold text-white">Schedule a Meeting</h1>
            <p className="text-sm text-blue-100 mt-1">
              with <strong>{data?.agent.name}</strong>
            </p>
          </div>
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-brand" />
              <span className="font-semibold text-slate-800">{data?.request.title}</span>
            </div>
            {data?.request.description && (
              <p className="text-xs text-slate-500 mt-2">{data.request.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span>Type: {data?.request.eventType}</span>
              <span>Duration: {data?.request.durationMinutes} min</span>
              <span>Timezone: {data?.request.timezone}</span>
            </div>
          </div>

          {/* Invitation actions */}
          <div className="p-5">
            {proposedTimeLabel ? (
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-500 mb-1">Proposed Time</p>
                <p className="text-sm font-bold text-slate-800">{proposedTimeLabel}</p>
                <p className="text-xs text-slate-500 mt-0.5">({data?.request.timezone})</p>
              </div>
            ) : (
              <p className="text-xs text-slate-500 mb-3">
                No fixed time was proposed — pick a slot that works for you below, or request a new time.
              </p>
            )}
            {/* Mandatory rejection reason — presets + custom ("Other") */}
            <div className="mb-3">
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Reason for declining (required)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {DECLINE_PRESETS.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setDeclinePreset(preset); setDeclineReason(preset); }}
                    className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition-all cursor-pointer ${declinePreset === preset ? 'bg-rose-50 border-rose-300 text-rose-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                  >
                    {preset}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setDeclinePreset('Other'); setDeclineReason(''); }}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border transition-all cursor-pointer ${declinePreset === 'Other' ? 'bg-rose-50 border-rose-300 text-rose-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                >
                  Other
                </button>
              </div>
              <textarea
                rows={2}
                value={declineReason}
                onChange={e => { setDeclineReason(e.target.value); if (declinePreset !== 'Other') setDeclinePreset('Other'); }}
                placeholder={declinePreset === 'Other' ? 'Please explain why you are declining…' : 'Please provide a reason for declining this meeting…'}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500/20 resize-none"
              />
              {!declineReason.trim() && <p className="text-[10px] text-rose-500 font-semibold mt-1">A reason is required to decline.</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => {
                  if (proposedTimeLabel) respond('accept');
                  else { setShowPicker(true); setConfirmError(null); }
                }}
                disabled={responding}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
              >
                <CheckCircle className="h-3.5 w-3.5" /> {proposedTimeLabel ? 'Accept' : 'Choose a Time'}
              </button>
              <button
                onClick={() => respond('decline')}
                disabled={responding || !declineReason.trim()}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
              >
                <AlertCircle className="h-3.5 w-3.5" /> Decline
              </button>
              <button
                onClick={() => setShowPicker(true)}
                disabled={responding}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-brand hover:opacity-90 text-white text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
              >
                <Clock className="h-3.5 w-3.5" /> {showPicker ? 'Choosing…' : 'New Time'}
              </button>
            </div>
            {!proposedTimeLabel && !showPicker && (
              <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                <Clock className="h-3 w-3" /> No fixed time was proposed — choose an available slot and it will be confirmed automatically.
              </p>
            )}
            {confirmError && (
              <div className="flex items-center gap-2 text-xs font-bold text-red-700 bg-red-50 px-4 py-2 rounded-xl mt-3">
                <AlertCircle className="h-3.5 w-3.5" />
                {confirmError}
              </div>
            )}
          </div>
        </div>

        {showPicker && (
        <>
        {monthNotice && (
          <div className="mb-3 flex items-center gap-2 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-xl">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {monthNotice}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Calendar */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 rounded-lg cursor-pointer">
                <ChevronLeft className="h-4 w-4 text-slate-600" />
              </button>
              <span className="text-sm font-bold text-slate-800">
                {MONTH_NAMES[currentMonth]} {currentYear}
              </span>
              <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 rounded-lg cursor-pointer">
                <ChevronRight className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-7 gap-1">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-bold text-slate-400 py-1">{d}</div>
                ))}
                {dates.map((dateStr, i) => {
                  if (!dateStr) return <div key={`e-${i}`} />;
                  const available = isDateAvailable(dateStr);
                  const isSelected = dateStr === selectedDate;
                  const dayNum = dateStr.split('-')[2];
                  return (
                    <button
                      key={dateStr}
                      disabled={!available}
                      onClick={() => { setSelectedDate(dateStr); setSelectedTime(null); }}
                      className={`text-center py-2 text-xs rounded-lg transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-brand text-white font-bold'
                          : available
                          ? 'hover:bg-blue-50 text-slate-700 font-medium'
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                    >
                      {parseInt(dayNum)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Time slots */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <span className="text-sm font-bold text-slate-800">
                {selectedDate
                  ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                  : 'Select a date'}
              </span>
            </div>
            <div className="p-3 max-h-[320px] overflow-y-auto">
              {!selectedDate ? (
                <p className="text-xs text-slate-400 text-center py-8">Please select a date from the calendar</p>
              ) : timeSlots.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">No available time slots for this date</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {timeSlots.map(time => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`px-3 py-2.5 text-xs rounded-xl border transition-all cursor-pointer text-center ${
                        selectedTime === time
                          ? 'border-brand bg-blue-50 text-brand font-bold'
                          : 'border-slate-200 hover:border-brand/30 text-slate-600 font-medium'
                      }`}
                    >
                      {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedDate && selectedTime && (
          <div className="bg-white rounded-2xl shadow-xl p-5 mt-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <p className="text-sm font-bold text-slate-800">Confirm this new time</p>
                <p className="text-xs text-slate-500">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                  })} at{' '}
                  {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  {' '}({data?.request.timezone})
                </p>
                <p className="text-[10px] text-emerald-600 mt-1">This slot is from the organizer's available calendar — it will be confirmed immediately.</p>
              </div>
              <button
                onClick={() => respond('new_time')}
                disabled={responding}
                className="px-6 py-2.5 btn-primary text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {responding ? (
                  <><Loader className="h-4 w-4 animate-spin" /> Confirming...</>
                ) : (
                  <><CheckCircle className="h-4 w-4" /> Confirm New Time</>
                )}
              </button>
            </div>
            {confirmError && (
              <div className="flex items-center gap-2 text-xs font-bold text-red-700 bg-red-50 px-4 py-2 rounded-xl">
                <AlertCircle className="h-3.5 w-3.5" />
                {confirmError}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
